"use client";

import { useState, useRef, useId, useLayoutEffect } from "react";
import { Button, Alert } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { composerStyles } from "../forum-composer.styles";
import type { ForumComposeComment } from "../forum.types";
export function CommentComposer({
  parentId,
  label,
  compose,
}: Readonly<{
  parentId: string | null;
  label: string;
  compose: ForumComposeComment;
}>) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const fieldId = useId();
  const busy = compose.busyParentId !== undefined || submitting;
  const error =
    compose.error?.parentId === parentId ? compose.error.message : null;
  const dirty = body !== "";
  const { onDirtyChange } = compose;
  useLayoutEffect(() => {
    onDirtyChange?.({ parentId, dirty });
  }, [dirty, onDirtyChange, parentId]);
  useLayoutEffect(() => {
    return () => onDirtyChange?.({ parentId, dirty: false });
  }, [onDirtyChange, parentId]);

  function discard() {
    setBody("");
    setOpen(false);
    trigger.current?.focus();
  }

  return (
    <div>
      <Button
        size="sm"
        variant="quiet"
        ref={trigger}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {label}
      </Button>
      {open && (
        <form
          {...stylex.props(composerStyles.form)}
          onSubmit={async (event) => {
            event.preventDefault();

            if (busy || !body.trim()) return;

            setSubmitting(true);
            const accepted = await compose.onSubmit({ parentId, body });
            setSubmitting(false);

            if (accepted) {
              discard();
            }
          }}
        >
          <label {...stylex.props(composerStyles.field)} htmlFor={fieldId}>
            {label}
            <textarea
              id={fieldId}
              rows={3}
              required
              value={body}
              disabled={busy}
              onChange={(event) => setBody(event.target.value)}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              {...stylex.props(composerStyles.textarea)}
            />
          </label>
          {error && (
            <Alert tone="danger" announcement="assertive">
              {error}
            </Alert>
          )}
          <div {...stylex.props(composerStyles.actions)}>
            <Button
              type="submit"
              variant="primary"
              disabled={busy || !body.trim()}
            >
              {submitting ? "Posting…" : "Comment"}
            </Button>
            <Button disabled={busy} onClick={discard}>
              Discard
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
